import { Box, Flex, Heading, IconButton, Text } from "@chakra-ui/react";
import { ArrowLeft } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import Footer from "@/components/Footer";
import { ROUTES } from "@/config/routes";
import {
  SETTLEMENT_ADJUSTMENTS_TABLE_COLUMNS,
  SETTLEMENT_INVOICES_TABLE_COLUMNS,
} from "@/config/tables";
import AdjustmentsTable from "../components/tables/AdjustmentsTable";
import InvoicesTable from "../components/tables/InvoicesTable";
import { useSettlementAdjustments } from "../hooks/useSettlementAdjustments";
import { useSettlementInvoices } from "../hooks/useSettlementInvoices";

export default function SettlementDetailContainer(): JSX.Element {
  const { settlementId } = useParams<{ settlementId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [date, cycle] = (settlementId ?? "").split("_");
  const type =
    searchParams.get("detail") === "adjustments" ? "adjustments" : "invoices";

  const detailProps = date && cycle ? { date, cycle } : undefined;

  const { invoices, isLoading: isLoadingInvoices } =
    useSettlementInvoices(detailProps);
  const { adjustments, isLoading: isLoadingAdjustments } =
    useSettlementAdjustments(detailProps);

  const handleBack = () => {
    const historyState = window.history.state as { idx?: number } | null;
    if (historyState && (historyState.idx ?? 0) > 0) {
      navigate(-1);
    } else {
      navigate(ROUTES.DASHBOARD.SETTLEMENTS);
    }
  };

  const title =
    type === "invoices" ? "Valor recaudado" : "Valor abonado";

  return (
    <Box
      p={{ base: "1rem", md: "1rem 3rem" }}
      width="100%"
      height="100%"
      display="flex"
      flexDirection="column"
      gap="1.5rem"
      overflow="auto"
    >
      <Box
        display="flex"
        flexDirection={{ base: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ base: "stretch", md: "center" }}
        bg="white"
        p=".5rem 1rem"
        m={{ md: "-1rem -3rem 0 -3rem", base: "-1rem" }}
        borderBottom="1.5px solid #ebecef80"
        gap={4}
      >
        <Flex alignItems="center" gap={2}>
          <IconButton
            aria-label="Volver"
            size="sm"
            variant="ghost"
            onClick={handleBack}
            color="gray.400"
            _hover={{ bg: "gray.50", color: "gray.800" }}
            borderRadius="full"
          >
            <ArrowLeft size={20} />
          </IconButton>
          <Box>
            <Heading size="xl" fontWeight="semibold">
              {title}
            </Heading>
            <Text
              textStyle="xs"
              color="gray.500"
              fontWeight="light"
              display={{ base: "none", md: "block" }}
            >
              Conciliación del {date}
            </Text>
          </Box>
        </Flex>
      </Box>

      <Box display="flex" flexDirection="column" gap="1.5rem">
        {type === "invoices" ? (
          <InvoicesTable invoices={invoices} isLoading={isLoadingInvoices} />
        ) : (
          <AdjustmentsTable
            adjustments={adjustments}
            isLoading={isLoadingAdjustments}
          />
        )}
      </Box>

      {type === "invoices" ? (
        <Footer
          tableProps={{
            fileName: "recaudos-conciliaciones",
            items: invoices,
            columns: SETTLEMENT_INVOICES_TABLE_COLUMNS,
          }}
        />
      ) : (
        <Footer
          tableProps={{
            fileName: "ajustes-conciliaciones",
            items: adjustments,
            columns: SETTLEMENT_ADJUSTMENTS_TABLE_COLUMNS,
          }}
        />
      )}
    </Box>
  );
}
